import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { useCompanySettings, useUpdateCompanySettings } from '@/hooks/use-settings';
import type { TicketElementKey } from '@erp/data-access/interfaces';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input, Label,
  PageHeader,
} from '@erp/ui';
import { Save, Check, RotateCcw, GripVertical, AlertTriangle } from '@/components/icons';

const ALL_ELEMENTS: TicketElementKey[] = ['companyName', 'name', 'reference', 'price', 'barcode'];

const DEFAULTS = {
  ticketWidthMm: 40,
  ticketHeightMm: 20,
  ticketShowName: true,
  ticketShowPrice: true,
  ticketPriceType: 'ttc' as const,
  ticketShowReference: true,
  ticketShowBarcode: true,
  ticketShowCompanyName: false,
  ticketCurrencySymbol: 'DT',
  ticketElementOrder: [...ALL_ELEMENTS] as TicketElementKey[],
};

const ticketSchema = z.object({
  ticketWidthMm: z.coerce.number().min(10).max(200).default(DEFAULTS.ticketWidthMm),
  ticketHeightMm: z.coerce.number().min(10).max(200).default(DEFAULTS.ticketHeightMm),
  ticketShowName: z.boolean().default(DEFAULTS.ticketShowName),
  ticketShowPrice: z.boolean().default(DEFAULTS.ticketShowPrice),
  ticketPriceType: z.enum(['ht', 'ttc']).default(DEFAULTS.ticketPriceType),
  ticketShowReference: z.boolean().default(DEFAULTS.ticketShowReference),
  ticketShowBarcode: z.boolean().default(DEFAULTS.ticketShowBarcode),
  ticketShowCompanyName: z.boolean().default(DEFAULTS.ticketShowCompanyName),
  ticketCurrencySymbol: z.string().min(1).max(8).default(DEFAULTS.ticketCurrencySymbol),
  ticketElementOrder: z.array(z.enum(ALL_ELEMENTS as [TicketElementKey, ...TicketElementKey[]])).default([...ALL_ELEMENTS]),
});

type FormData = z.infer<typeof ticketSchema>;

const SHOW_FIELD_MAP: Record<TicketElementKey, keyof FormData> = {
  companyName: 'ticketShowCompanyName',
  name: 'ticketShowName',
  reference: 'ticketShowReference',
  price: 'ticketShowPrice',
  barcode: 'ticketShowBarcode',
};

const ELEMENT_LABEL_KEY: Record<TicketElementKey, string> = {
  companyName: 'settings.articleTickets.elements.companyName',
  name: 'settings.articleTickets.elements.name',
  reference: 'settings.articleTickets.elements.reference',
  price: 'settings.articleTickets.elements.price',
  barcode: 'settings.articleTickets.elements.barcode',
};

export function ArticleTicketSettingsPage() {
  const { t } = useTranslation();
  const { data: settings, isLoading } = useCompanySettings();
  const updateMutation = useUpdateCompanySettings();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    register, handleSubmit, reset, control, watch, setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<FormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(ticketSchema) as any,
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (settings) {
      reset({
        ticketWidthMm: settings.ticketWidthMm,
        ticketHeightMm: settings.ticketHeightMm,
        ticketShowName: settings.ticketShowName,
        ticketShowPrice: settings.ticketShowPrice,
        ticketPriceType: settings.ticketPriceType,
        ticketShowReference: settings.ticketShowReference,
        ticketShowBarcode: settings.ticketShowBarcode,
        ticketShowCompanyName: settings.ticketShowCompanyName,
        ticketCurrencySymbol: settings.ticketCurrencySymbol,
        ticketElementOrder: normalizeOrder(settings.ticketElementOrder),
      });
    }
  }, [settings, reset]);

  const values = watch();
  const currentOrder = values.ticketElementOrder ?? DEFAULTS.ticketElementOrder;

  const moveElement = (from: number, to: number) => {
    const next = [...currentOrder];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    setValue('ticketElementOrder', next, { shouldDirty: true });
  };

  const onSubmit = async (data: FormData) => {
    setSaveError(null);
    try {
      await updateMutation.mutateAsync({
        ...data,
        ticketElementOrder: normalizeOrder(data.ticketElementOrder),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setSaveError(formatError(err));
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('settings.articleTickets.title')}
        subtitle={t('settings.articleTickets.description')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('settings.articleTickets.dimensionsSection')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="ticketWidthMm">{t('settings.articleTickets.widthMm')}</Label>
                    <div className="flex items-center gap-2">
                      <Input id="ticketWidthMm" type="number" step="1" min={10} max={200} {...register('ticketWidthMm')} />
                      <span className="text-sm text-muted-foreground">mm</span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ticketHeightMm">{t('settings.articleTickets.heightMm')}</Label>
                    <div className="flex items-center gap-2">
                      <Input id="ticketHeightMm" type="number" step="1" min={10} max={200} {...register('ticketHeightMm')} />
                      <span className="text-sm text-muted-foreground">mm</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{t('settings.articleTickets.dimensionsHint')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('settings.articleTickets.fieldsSection')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">{t('settings.articleTickets.dragHint')}</p>
                <ElementOrderList
                  order={currentOrder}
                  values={values}
                  onMove={moveElement}
                  onToggle={(key, visible) => setValue(SHOW_FIELD_MAP[key], visible, { shouldDirty: true })}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('settings.articleTickets.priceSection')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('settings.articleTickets.priceType')}</Label>
                  <Controller
                    name="ticketPriceType"
                    control={control}
                    render={({ field }) => (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={field.value === 'ttc' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange('ttc')}
                        >
                          {t('settings.articleTickets.priceTypeTtc')}
                        </Button>
                        <Button
                          type="button"
                          variant={field.value === 'ht' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => field.onChange('ht')}
                        >
                          {t('settings.articleTickets.priceTypeHt')}
                        </Button>
                      </div>
                    )}
                  />
                </div>
                <div className="space-y-1.5 max-w-xs">
                  <Label htmlFor="ticketCurrencySymbol">{t('settings.articleTickets.currencySymbol')}</Label>
                  <Input id="ticketCurrencySymbol" {...register('ticketCurrencySymbol')} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">{t('settings.articleTickets.previewSection')}</CardTitle>
              </CardHeader>
              <CardContent>
                <TicketPreview
                  widthMm={Number(values.ticketWidthMm) || 40}
                  heightMm={Number(values.ticketHeightMm) || 20}
                  order={currentOrder}
                  visible={{
                    companyName: values.ticketShowCompanyName,
                    name: values.ticketShowName,
                    reference: values.ticketShowReference,
                    price: values.ticketShowPrice,
                    barcode: values.ticketShowBarcode,
                  }}
                  currencySymbol={values.ticketCurrencySymbol || 'DT'}
                  companyName={settings?.companyName ?? 'Ma société'}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {saveError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="font-medium">{t('settings.articleTickets.saveFailed')}</p>
              <p className="text-xs break-all">{saveError}</p>
              <p className="text-xs text-muted-foreground">{t('settings.articleTickets.saveFailedHint')}</p>
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={() => reset(DEFAULTS)}>
            <RotateCcw size={14} className="me-1.5" />
            {t('settings.articleTickets.resetDefaults')}
          </Button>
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {saved ? (
              <><Check size={14} className="me-1.5" />{t('settings.articleTickets.saved')}</>
            ) : isSubmitting ? (
              t('common.loading')
            ) : (
              <><Save size={14} className="me-1.5" />{t('common.save')}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err == null) return 'Unknown error';
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const parts = [e['message'], e['details'], e['hint'], e['code']].filter(Boolean);
    if (parts.length > 0) return parts.join(' — ');
    try { return JSON.stringify(err); } catch { return String(err); }
  }
  return String(err);
}

function normalizeOrder(order: TicketElementKey[] | undefined): TicketElementKey[] {
  const seen = new Set<TicketElementKey>();
  const next: TicketElementKey[] = [];
  (order ?? []).forEach((k) => {
    if (ALL_ELEMENTS.includes(k) && !seen.has(k)) { next.push(k); seen.add(k); }
  });
  ALL_ELEMENTS.forEach((k) => { if (!seen.has(k)) next.push(k); });
  return next;
}

interface ElementOrderListProps {
  order: TicketElementKey[];
  values: FormData;
  onMove: (from: number, to: number) => void;
  onToggle: (key: TicketElementKey, visible: boolean) => void;
}

function ElementOrderList({ order, values, onMove, onToggle }: ElementOrderListProps) {
  const { t } = useTranslation();
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  return (
    <div className="space-y-1">
      {order.map((key, idx) => {
        const visible = Boolean(values[SHOW_FIELD_MAP[key]]);
        const isDragging = dragIdx === idx;
        const isDropTarget = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
        return (
          <div key={key} className="relative">
            {isDropTarget && dragIdx !== null && dragIdx > idx && (
              <div className="absolute -top-0.5 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
            <div
              draggable
              onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx); }}
              onDragLeave={() => setDragOverIdx(null)}
              onDrop={() => { if (dragIdx !== null && dragIdx !== idx) onMove(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); }}
              onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
              className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm transition-all cursor-grab active:cursor-grabbing select-none ${isDragging ? 'opacity-30 scale-95' : 'hover:bg-accent/50'} ${isDropTarget ? 'border-primary bg-primary/5' : ''}`}
            >
              <GripVertical size={14} className="text-muted-foreground/60 shrink-0" />
              <span className="flex-1">{t(ELEMENT_LABEL_KEY[key])}</span>
              <Checkbox
                checked={visible}
                onCheckedChange={(v) => onToggle(key, Boolean(v))}
                onClick={(e) => e.stopPropagation()}
                aria-label={t(ELEMENT_LABEL_KEY[key])}
              />
            </div>
            {isDropTarget && dragIdx !== null && dragIdx < idx && (
              <div className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface TicketPreviewProps {
  widthMm: number;
  heightMm: number;
  order: TicketElementKey[];
  visible: Record<TicketElementKey, boolean>;
  currencySymbol: string;
  companyName: string;
}

function TicketPreview(props: TicketPreviewProps) {
  const SCALE = 4;
  const width = props.widthMm * SCALE;
  const height = props.heightMm * SCALE;

  const renderElement = (key: TicketElementKey) => {
    if (!props.visible[key]) return null;
    switch (key) {
      case 'companyName':
        return (
          <div key={key} className="font-semibold leading-tight text-center truncate w-full" style={{ fontSize: Math.max(6, height * 0.07) }}>
            {props.companyName}
          </div>
        );
      case 'name':
        return (
          <div key={key} className="font-bold leading-tight text-center truncate w-full" style={{ fontSize: Math.max(7, height * 0.1) }}>
            Article démo
          </div>
        );
      case 'reference':
        return (
          <div key={key} className="text-zinc-600 leading-tight text-center truncate w-full" style={{ fontSize: Math.max(6, height * 0.07) }}>
            ART-001
          </div>
        );
      case 'price':
        return (
          <div key={key} className="font-bold leading-tight text-center truncate w-full" style={{ fontSize: Math.max(8, height * 0.12) }}>
            12,500 {props.currencySymbol}
          </div>
        );
      case 'barcode':
        return (
          <div key={key} className="bg-zinc-900 w-[85%] flex items-center justify-center mt-0.5" style={{ height: Math.max(10, height * 0.3) }}>
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, #000 0, #000 2px, #fff 2px, #fff 4px, #000 4px, #000 5px, #fff 5px, #fff 8px)',
              }}
            />
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="bg-white dark:bg-zinc-100 border border-border rounded-sm shadow-sm overflow-hidden flex flex-col items-center justify-center text-zinc-900 p-1"
        style={{ width, height, fontSize: Math.max(7, height * 0.08) }}
      >
        {props.order.map((k) => renderElement(k))}
      </div>
      <div className="text-xs text-muted-foreground">
        {props.widthMm} × {props.heightMm} mm
      </div>
    </div>
  );
}
